import { ref } from 'vue';

import { apiFetch } from '@ja/portal-query';

export interface CrudTagOption {
    id: string;
    label: string;
    color: string | null;
    usageCount?: number;
}

interface CrudTagMutationResult {
    id: string;
    label: string;
    color: string | null;
    created?: boolean;
    updated?: boolean;
}

export function useCrudTags(model: string) {
    const suggestions = ref<CrudTagOption[]>([]);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const error = ref<string | null>(null);

    async function fetchSuggestions(query = ''): Promise<CrudTagOption[]> {
        if (!model) {
            suggestions.value = [];
            return [];
        }

        isLoading.value = true;
        error.value = null;

        try {
            const params = new URLSearchParams();
            if (query.trim() !== '') {
                params.set('q', query.trim());
            }
            const queryString = params.toString();
            const url = `/crud/${model}/tags${queryString ? `?${queryString}` : ''}`;
            const result = await apiFetch<CrudTagOption[]>(url);
            suggestions.value = Array.isArray(result) ? result : [];
            return suggestions.value;
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to load tags.';
            return [];
        } finally {
            isLoading.value = false;
        }
    }

    async function createTag(label: string, color: string | null = null): Promise<CrudTagMutationResult | null> {
        isSaving.value = true;
        error.value = null;

        try {
            return await apiFetch<CrudTagMutationResult>(`/crud/${model}/tags`, {
                method: 'POST',
                body: JSON.stringify({ label, color }),
            });
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to create tag.';
            return null;
        } finally {
            isSaving.value = false;
        }
    }

    async function updateTag(tagId: string, label: string, color: string | null = null): Promise<CrudTagMutationResult | null> {
        isSaving.value = true;
        error.value = null;

        try {
            return await apiFetch<CrudTagMutationResult>(`/crud/${model}/tags/${tagId}`, {
                method: 'PATCH',
                body: JSON.stringify({ label, color }),
            });
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to update tag.';
            return null;
        } finally {
            isSaving.value = false;
        }
    }

    async function deleteTag(tagId: string): Promise<boolean> {
        isSaving.value = true;
        error.value = null;

        try {
            await apiFetch<{ deleted: boolean }>(`/crud/${model}/tags/${tagId}`, {
                method: 'DELETE',
            });
            return true;
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to delete tag.';
            return false;
        } finally {
            isSaving.value = false;
        }
    }

    return {
        suggestions,
        isLoading,
        isSaving,
        error,
        fetchSuggestions,
        createTag,
        updateTag,
        deleteTag,
    };
}
